<mat-expansion-panel hideToggle [class]="fullPanelClass()" (expandedChange)="isExpanded.set($event)">
  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
    <div class="flex items-center gap-2 w-full min-w-0 px-3 py-2">
      <!-- Social badge — platform color -->
      <app-social-badge
        color="var(--shikenso-base-white)"
        [borderRadius]="8"
        [iconSize]="14"
        [size]="36"
        [type]="cta().platformFeature!"
      />

      <!-- Name + Global / Shared tag -->
      <div class="flex items-center gap-8 flex-1 min-w-0">
        <app-editable-title
          fontClasses="text-base font-medium"
          [icon]="null"
          [maxWidth]="180"
          [title]="cta().name || ('shared.platformFeatures.' + cta().platformFeature | translate)"
        />

        @if (!hideShareStatusIcon()) {
          @let tag = ctaTag();

          @if (tag) {
            <div class="flex items-center gap-1 text-xs text-shikenso-purple-base-60 shrink-0">
              <app-icon
                [src]="
                  tag === 'global' || tag === 'globalCustomized'
                    ? 'icons/world.svg'
                    : tag === 'individual'
                      ? 'icons/user-star.svg'
                      : 'icons/creators.svg'
                "
                [size]="16"
              />
              <span>
                {{
                  (tag === 'global'
                    ? 'pages.campaignForm.sections.creatorsAndCtas.globalTag'
                    : tag === 'globalCustomized'
                      ? 'pages.campaignForm.sections.creatorsAndCtas.globalCustomizedTag'
                      : tag === 'shared'
                        ? 'pages.campaignForm.sections.creatorsAndCtas.sharedTag'
                        : tag === 'sharedCustomized'
                          ? 'pages.campaignForm.sections.creatorsAndCtas.sharedCustomizedTag'
                          : 'pages.campaignForm.sections.creatorsAndCtas.individualTag'
                  ) | translate
                }}
              </span>
            </div>
          }
        }
      </div>

      <!-- Count buttons + projected menu (stopPropagation so they don't toggle the panel) -->
      @let existingDeliverablesCount = getExistingDeliverablesCount()(cta().id!, creatorId());
      @let existingExecutionsCount = getExistingExecutionsCount()(cta().id!, creatorId());
      @let existingKpisCount = getExistingKpisCount()(cta().id!, creatorId());

      <div class="flex items-center gap-2 ml-auto font-medium shrink-0" (click)="$event.stopPropagation()">
        <button
          mat-flat-button
          type="button"
          class="mat-slim-button creator-cta-button !bg-shikenso-pink-100-24 shrink-0 !min-w-0"
          (click)="addDeliverablesForCta.emit(cta().id!)"
        >
          <div class="flex items-center gap-1 whitespace-nowrap">
            <app-icon alt="deliverables" src="icons/clipboard-list.svg" [size]="10" />
            <span>
              {{ existingDeliverablesCount }}
              {{
                existingDeliverablesCount === 1
                  ? ('pages.campaignForm.sections.creatorsAndCtas.deliverable' | translate)
                  : ('pages.campaignForm.sections.creatorsAndCtas.deliverables' | translate)
              }}
            </span>
          </div>
        </button>

        <button
          mat-flat-button
          type="button"
          class="mat-slim-button creator-cta-button !bg-shikenso-pink-100-24 shrink-0 !min-w-0"
          (click)="addExecutionsForCta.emit(cta().id!)"
        >
          <div class="flex items-center gap-1 whitespace-nowrap">
            <app-icon alt="executions" src="icons/calendar-event.svg" [size]="10" />
            <span>
              {{ existingExecutionsCount === null ? '0' : existingExecutionsCount || '∞' }}
              {{
                existingExecutionsCount === 1
                  ? ('pages.campaignForm.sections.creatorsAndCtas.rep' | translate)
                  : ('pages.campaignForm.sections.creatorsAndCtas.reps' | translate)
              }}
            </span>
          </div>
        </button>

        <button
          mat-flat-button
          type="button"
          class="mat-slim-button creator-cta-button !bg-shikenso-pink-100-24 shrink-0 !min-w-0"
          (click)="addKpisForCta.emit(cta().id!)"
        >
          <div class="flex items-center gap-1 whitespace-nowrap">
            <app-icon alt="kpis" src="icons/chart-histogram.svg" [size]="10" />
            <span>
              {{ existingKpisCount }}
              {{
                existingKpisCount === 1
                  ? ('pages.campaignForm.sections.creatorsAndCtas.kpi' | translate)
                  : ('pages.campaignForm.sections.creatorsAndCtas.kpis' | translate)
              }}
            </span>
          </div>
        </button>

        <!-- Menu content projected from parent -->
        <ng-content select="[ctaMenu]" />
      </div>

      <!-- Expansion chevron -->
      <app-icon
        alt="expand"
        class="w-4 h-4 transition-transform duration-200 shrink-0"
        [class.-rotate-180]="isExpanded()"
        src="icons/chevron-down.svg"
      />
    </div>
  </mat-expansion-panel-header>

  <!-- Expanded body -->
  <div class="flex flex-col gap-3 px-4 py-3">
    <!-- Deliverables -->
    @if (deliverables().length) {
      <div class="flex flex-col text-xs">
        <span class="font-bold">
          {{ 'pages.campaignForm.sections.creatorsAndCtas.ctaSummary.deliverables' | translate }}
        </span>

        <div class="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
          @for (d of deliverables(); track d.id; let last = $last) {
            <span
              class="cursor-default"
              matTooltipPosition="above"
              [matTooltip]="'shared.deliverableTypes.' + d.type | translate"
            >
              {{ d.displayName }}
            </span>

            @if (!last) {
              <span class="mx-0.5">•</span>
            }
          }
        </div>
      </div>
    }

    <!-- Frequency -->
    @if (executions().length) {
      <div class="flex flex-col text-xs">
        <span class="font-bold">
          {{ 'pages.campaignForm.sections.creatorsAndCtas.ctaSummary.frequency' | translate }}
        </span>

        <div class="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
          @for (exec of executions(); track exec.id; let last = $last) {
            @if (exec.label) {
              <span>{{ exec.label }}</span>

              @if (exec.durationLabel) {
                <span class="text-[10px] text-shikenso-purple-base-60">({{ exec.durationLabel }})</span>
              }

              @if (!last) {
                <span class="mx-0.5">•</span>
              }
            }
          }
        </div>
      </div>
    }

    <!-- KPIs -->
    @if (allKpis().length) {
      <div class="flex flex-col text-xs">
        <span class="font-bold">
          {{ 'pages.campaignForm.sections.creatorsAndCtas.ctaSummary.kpis' | translate }}
        </span>

        <div class="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
          @for (kpi of allKpis(); track kpi.id; let last = $last) {
            <span>{{ kpi.translatedName }}: {{ kpi.expectedValue ?? 0 | numberSuffix }}</span>

            @if (!last) {
              <span class="mx-0.5">•</span>
            }
          }
        </div>
      </div>
    }
  </div>
</mat-expansion-panel>
